import { SwalDialogService } from 'src/app/core/shared/services/swal-dialog.service';
import { LoaderService } from 'src/app/service/loader/loader.service';
import { AppSettingsService } from 'src/app/core/app-settings.service';
import { Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

export interface FileData {
  uploadedFileName: string;
  originalFileName: string;
  file: null | any;
  id: string;
}

@Injectable({
  providedIn: 'root'
})
export class AttachmentUploadService {
  fileDataSignal = signal<FileData[]>([]);
  uploadedFilesCount = signal<number>(0);
  failedFilesCount = signal<number>(0);
  totalFilesCount = signal<number>(0);
  uploadProgress = signal<number>(0);
  uploadedSuccess = signal<boolean>(false);
  fileDataArray: FileData[] = [];
  constructor(
    private appSettingsService: AppSettingsService,
    private loaderService: LoaderService,
    private swalDialogService: SwalDialogService
  ) {}

  // Getter for fileDataSignal
  getFileDataSignal() {
    return this.fileDataSignal(); // Get the current value of the signal
  }

  // Method to push new file data into the existing signal array
  pushFileDataSignal(data: FileData[]) {
    this.fileDataSignal.set(data); // Push new data into the array
  }

  // Reset fileDataSignal
  resetSignals() {
    this.fileDataSignal.set([]); // Reset to an empty array
    this.uploadedFilesCount.set(0);
    this.failedFilesCount.set(0);
    this.totalFilesCount.set(0);
    this.uploadProgress.set(0);
    this.uploadedSuccess.set(false);
    this.fileDataArray = [];
  }

  // Main function to handle file upload
  async onFileUpload(event: any, fileTypes: string[] = ['pdf', 'png', 'jpeg']) {
    this.resetSignals();
    const inputElement = event.target as HTMLInputElement;
    const files = inputElement?.files;

    if (!files || files.length === 0) {
      this.swalDialogService.showMessage({
        title: 'No file selected',
        icon: 'error'
      });
      return;
    }

    const allowedFileTypes = fileTypes.map((type) => this.getMimeType(type));
    const invalidFiles = Array.from(files).filter(
      (file) => !allowedFileTypes.includes(file.type)
    );
    if (invalidFiles.length > 0) {
      this.swalDialogService.showMessage({
        title: `Invalid file types: ${invalidFiles
          .map((file) => file.name)
          .join(', ')}. Allowed types are ${fileTypes.join(', ')}`,
        icon: 'error'
      });
      this.uploadedSuccess.set(true);
      return;
    }

    // Update total files count
    this.totalFilesCount.set(files.length);

    this.loaderService.showFile();

    // Process files one by one sequentially
    for (let index = 0; index < files.length; index++) {
      await this.processFile(files[index], index + 1); // Await each file's upload process
    }
    if (inputElement) {
      inputElement.value = ''; // Clear file input after processing
    }
  }

  getMimeType(extension: string): string {
    const mimeTypes: { [key: string]: string } = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg' // Handle 'jpg' as well for flexibility
    };

    return mimeTypes[extension] || ''; // Return the mapped MIME type or an empty string if not found
  }

  async processFile(file: File, fileIndex: number): Promise<void> {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        try {
          await this.onUpload(file, reader.result, fileIndex);
          resolve(); // Resolve the promise after the file is uploaded
        } catch (error) {
          reject(error); // Reject if there is any error during upload
        }
      };

      reader.onerror = () => {
        reject(new Error('File reading error.'));
      };
    });
  }

  async onUpload(
    file: File,
    fileData: string | ArrayBuffer | null,
    fileIndex: number
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await firstValueFrom(
        this.appSettingsService.postAttachment(formData)
      );
      const uploadedFileName = res.fileName; // File name after upload
      const originalFileName = file.name; // Original file name from client

      if (uploadedFileName) {
        this.fileDataArray.push({
          uploadedFileName: uploadedFileName,
          originalFileName: originalFileName,
          file: fileData,
          id: this.appSettingsService.generate16DigitUUID()
        });
        setTimeout(() => {
          this.updateUploadProgress(fileIndex);
        }, 500);
      }
    } catch (error) {
      this.swalDialogService.showMessage({
        title: `Error uploading ${file.name}`,
        icon: 'error'
      });
      this.loaderService.hideFile();
      this.uploadedSuccess.set(true);
      this.failedFilesCount.set(this.failedFilesCount() + 1);
    } finally {
      //   this.loaderService.hideFile();
    }
  }

  // Update the progress of file uploads
  updateUploadProgress(currentFileIndex: number) {
    const progress = Math.round(
      (currentFileIndex / this.totalFilesCount()) * 100
    );
    this.uploadProgress.set(progress);

    // Update successfully uploaded files count
    this.uploadedFilesCount.set(currentFileIndex);

    // Notify user when all files are uploaded
    if (currentFileIndex === this.totalFilesCount()) {
      this.uploadedSuccess.set(true);
      this.pushFileDataSignal(this.fileDataArray);

      this.swalDialogService.showMessage({
        title: `All files uploaded. ${this.uploadedFilesCount()} succeeded, ${this.failedFilesCount()} failed.`
      });
      setTimeout(() => {
        this.resetSignals();
        this.loaderService.hideFile();
      }, 500);
    }
  }
}
